// 第三方库导入
import { createFileRoute } from '@tanstack/react-router'
import { useParams, useNavigate } from '@tanstack/react-router'
import {
  Steps,
  Form,
  Input,
  Picker,
  Radio,
  Space,
  Selector,
  Button,
  Toast,
  Popup,
  SearchBar,
  Modal,
  ErrorBlock,
} from 'antd-mobile'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

// 本地模块导入
import Body from '@/components/Body'
import {
  type Order,
  type regionType,
  type DraftObj,
  rules,
  type PositionCode,
  STEPS_CONFIG,
  type Course,
  AgreementType,
} from '@/types/info'
import {
  usePositionCodeList,
  usePositionCodeListHasInventory,
} from '@/hook/usePositionCodeList'
import AgreementService from '@/service/agreementService'
import { useCourse, useCourseRegionList } from '@/hook/useCourseList'
import apiClient from '@/utils/http'
import { updateDraft, useDraft } from '@/hook/useDraft'
import PositionCodeService, {
  type hasInventoryList,
} from '@/service/positionCodeService'
import { EnrollToast } from '@/utils/toastBus'
import type { ApiError } from '@/types/api'
import { ErrorMsg } from '@/types/error'
import RegionService from '@/service/regionService'
import useRefresh from '@/hook/useRefresh'
import { debounce } from 'lodash'
import { encodeOrderId } from '@/utils/orderIdEncoder'
import PrivacyPolicy from '@/components/PrivacyPolicy'

// #region 创建订单去重锁（同一课程30秒内且未返回前禁止重复提交）
const CREATE_ORDER_LOCK_PREFIX = 'create_order_lock_'
const inMemoryCreateOrderLock = new Map<number, number>()

function getCreateLockKey(courseId: number) {
  return `${CREATE_ORDER_LOCK_PREFIX}${courseId}`
}

function isCreateLocked(courseId: number): boolean {
  const now = Date.now()
  const expiryInMem = inMemoryCreateOrderLock.get(courseId)
  if (expiryInMem && now < expiryInMem) return true

  const key = getCreateLockKey(courseId)
  const str = localStorage.getItem(key)
  if (!str) return false
  const expiry = Number(str)
  if (!Number.isFinite(expiry)) {
    localStorage.removeItem(key)
    return false
  }
  if (now < expiry) {
    inMemoryCreateOrderLock.set(courseId, expiry)
    return true
  }
  localStorage.removeItem(key)
  inMemoryCreateOrderLock.delete(courseId)
  return false
}

function lockCreate(courseId: number, ttlMs = 30_000) {
  const expireAt = Date.now() + ttlMs
  inMemoryCreateOrderLock.set(courseId, expireAt)
  localStorage.setItem(getCreateLockKey(courseId), String(expireAt))
}

function unlockCreate(courseId: number) {
  inMemoryCreateOrderLock.delete(courseId)
  localStorage.removeItem(getCreateLockKey(courseId))
}
const KEY_WORD = '刷题班'
const SHI_YE_DAN_WEI = '事业单位'
// #endregion

/**
 * 课程报名输入页面组件
 * @returns 渲染后的 JSX 元素
 */
const RouteComponent: React.FC = () => {
  // 获取路由参数
  const { course_id } = useParams({
    from: PATH,
  })
  const navigate = useNavigate()
  const [form] = Form.useForm<DraftObj>()
  const agreementList = AgreementService.data
  const courseId = Number(course_id)
  // 相对孤立单一的状态
  const [popupVisible, setPopupVisible] = useState(false)
  const [isAgree, setIsAgree] = useState<boolean>(false)
  const [creatingOrder, setCreatingOrder] = useState<boolean>(false)
  const { isLoading: courseIsLoading, refetch: getCourse , data:course} =
    useCourse(courseId)
  const batchId = course?.batchId
  // #region 职位代码
  /**
   * 职位代码 ID 状态
   * 提取职位代码选择逻辑
   */
  const [positionCodeId, setPositionCodeId] = useState<number>()
  const { data: positionCodeList, isFetching: isFetchingPositionCode } =
    usePositionCodeList(batchId)
  const handlePositionCodeSelect = (selectedId: number) => {
    form?.setFieldValue('position_code_id', selectedId)
    setPositionCodeId(selectedId)
  }
  // #endregion

  // #region 校区
  /**
   * 校区 ID 状态
   * 提取校区选择逻辑
   */
  const [regionId, setRegionId] = useState<number>()
  const { data: regionList, refetch: refetchRegionList } =
    useCourseRegionList(courseId)
  const handleRegionSelect = async () => {
    if (!regionList) return
    if (!RegionService.checkHasInventory(regionList)) {
      Toast.show('库存不足：没有可用的地区')
      return
    }
    await Picker.prompt({
      columns: [
        regionList.map((region: regionType) => ({
          label: !region.hasInventory
            ? region.name + '(名额已满)'
            : region.name,
          value: region.id,
          key: region.id,
        })),
      ],
      onConfirm(value) {
        if (value) {
          const selectedRegionId = value[0] as number
          const selectedRegion = regionList.find(
            (region) => region.id === selectedRegionId,
          )
          if (selectedRegion?.hasInventory) {
            // 处理该地区名额已满的情况
            setRegionId(selectedRegionId)
            form?.setFieldValue('region_id', selectedRegionId)
          } else {
            Toast.show('该地区名额已满')
          }
        }
      },
    })
  }
  // #endregion

  // #region 守擂
  /**
   * 守擂状态
   * 守擂状态改变时，重置协议选择为空
   */
  const [isZhuangyuan, setIsZhuangyuan] = useState<
    'true' | 'false' | undefined
  >()
  const handleIsZhuangyuanChange = (value: 'true' | 'false' | undefined) => {
    setIsZhuangyuan(value)
    form.setFieldValue('agreement', [])
    updateDraftMutate({
      ...form.getFieldsValue(),
      agreement:[],
      is_agree: isAgree,
      course_id: courseId,
    })
  }
  // #endregion

  // #region 订单信息草稿
  /**
   * 订单信息草稿相关
   */
  const { mutate: updateDraftMutate } = updateDraft()
  const { data: draft, isLoading: isLoadingDraft } = useDraft(courseId)
  useEffect(() => {
    isLoadingDraft ? false : setIsAgree(draft?.is_agree ?? false)
  }, [isLoadingDraft])
  // #endregion

  const isLoading =
    isFetchingPositionCode || courseIsLoading || isLoadingDraft
  useEffect(() => {
    return () => {
      Toast.clear()
    }
  }, [])

  if (!courseId)
    return (
      <>
        <Space direction="vertical" block align="center">
          <ErrorBlock
            status="empty"
            style={{ alignItems: 'center' }}
            fullPage
          />
        </Space>
      </>
    )

  // #region 职位代码
  // let hasInventory = -1 // 初始化库存为 -1,表示未加载或未知
  let positionCodeName: string | undefined
  // 如果职位代码列表存在且未加载，则获取对应职位代码的库存
  if (positionCodeList && !isFetchingPositionCode && positionCodeId) {
    // 根据职位代码 ID 获取对应的库存
    let positionCode = PositionCodeService.getPositionCode(positionCodeId)
    positionCodeName = positionCode?.name
    // hasInventory = positionCode?.hasInventory ?? -1
  }
  const {
    data: hasInventoryList,
    refetch: refetchPositionCode,
    isFetching: isFetchingHasInventory,
  } = usePositionCodeListHasInventory(
    [positionCodeId],
    batchId,
  )
  // 获取职位代码表单显示文本
  const positionCodeFormText =
    isFetchingPositionCode ||
    courseIsLoading ||
    isLoadingDraft ||
    isFetchingHasInventory
      ? positionCodeText.loading
      : hasInventoryList !== undefined &&
          hasInventoryList.length > 0 &&
          hasInventoryList[0].hasInventory > 0
        ? (positionCodeName ?? positionCodeText.select)
        : positionCodeText.select
  // #endregion

  //#region 刷新处理
  useRefresh([refetchRegionList, refetchPositionCode])
  //#endregion

  // #region 校区
  // 获取校区表单显示文本
  const region = regionList?.find((region) => region.id === regionId)
  let regionFormText = courseIsLoading
    ? '正在加载'
    : region?.hasInventory
      ? region.name
      : '请选择授课地点'
  // #endregion

  // #region 处理表单提交
  const handleFinish = async (value: DraftObj) => {
    if (isZhuangyuan === undefined) {
      return
    }
    // 检查是否同意协议
    if (!isAgree) {
      EnrollToast.agree()
      return
    }
    const orderId = await handleSubmit({
      value: { ...value, is_zhuangyuan: isZhuangyuan },
      courseId: courseId,
      setPending: setCreatingOrder,
    })
    if (!orderId) {
      refetchPositionCode()
      getCourse()
      return
    }
    navigate({
      to: '/order/$orderId',
      params: {
        orderId: encodeOrderId(orderId),
      },
      replace: true,
    })
  }
  const handleOrderCreation = (value:DraftObj)=>{
    Modal.confirm({
      title: '确认提交订单',
      content: '请填写真实姓名与手机号，否则无法正常报名',
      confirmText: '确认提交',
      cancelText: '取消',
      onConfirm: async () => {
        await handleFinish(value)
      },
    })
  }
  // #endregion

  // #region 处理表单值变化
  const handleValuesChange = () => {
    updateDraftMutate({
      ...form.getFieldsValue(),
      is_agree: isAgree,
      course_id: courseId,
    })
  }
  // #endregion

  // #region 组件挂载时加载表单信息
  useEffect(() => {
    if (draft) {
      form.setFieldsValue(draft)
      setIsZhuangyuan(draft.is_zhuangyuan)
      setIsAgree(draft.is_agree ?? false)
      setPositionCodeId(draft.position_code_id)
      setRegionId(draft.region_id)
    }
  }, [draft])
  // #endregion

  // #region 职位代码 ID 或校区 ID 变化时存储表单值
  useEffect(() => {
    if (isLoading) return
    updateDraftMutate({
      ...form.getFieldsValue(),
      is_agree: isAgree,
      course_id: courseId,
    })
  }, [positionCodeId, regionId, isAgree])
  // #endregion
  const courseName = course?.courseName
  const AgreementOptions = (function (){
    const options = []
    if(courseName&&courseName.includes(SHI_YE_DAN_WEI)){
      options.push(agreementList[2]) // 只显示非协议
      return options
    }
    if(isZhuangyuan==='true'){
      options.push(agreementList[0]) // 状元协议
    }else{
      options.push(agreementList[1]) // 非状元协议
    }
    if(courseName&&!courseName.includes(KEY_WORD)){
      options.push(agreementList[2]) // 刷题班不显示非协议
    }
    return options
  })()
  // #region 组件渲染
  return (
    <Body>
      <div className="bg-white mb-[0.5rem]">
        <Steps current={0}>
          {STEPS_CONFIG.map((step, index) => (
            <Steps.Step key={index} title={step.title} />
          ))}
        </Steps>
      </div>
      <Form
        onFinish={handleOrderCreation}
        form={form}
        onValuesChange={handleValuesChange}
        onFinishFailed={() => EnrollToast.fillUp()}
      >
        <Form.Item label="课程名称">
          <Input value={courseName} readOnly />
        </Form.Item>
        <Form.Header>课程信息</Form.Header>
        <Form.Item
          name="position_code_id"
          label="职位代码"
          onClick={() => {
            setPopupVisible(true)
          }}
          rules={rules.position_code_id}
        >
          {positionCodeFormText.split('%').map((item, index) => (
            <div key={index}>{item}</div>
          ))}
        </Form.Item>
        <PositionCodePopup
          popupVisible={popupVisible}
          setPopupVisible={setPopupVisible}
          handleSelect={handlePositionCodeSelect}
          positionCodeList={positionCodeList ?? []}
          isLoading={isFetchingPositionCode}
          loadingView={
            <p className="text-[#A3A3A3] text-[15px] text-center">
              正在加载职位代码列表...
            </p>
          }
          batchId={batchId}
        />
        <Form.Item
          name="region_id"
          label="授课地点"
          style={{ position: 'relative' }}
          onClick={handleRegionSelect}
          rules={rules.region_id}
        >
          {regionFormText}
        </Form.Item>
        <Form.Header>
          个人信息
          <div className="text-[#FF5200] text-[0.8rem] mt-[10px]">
            请填写真实姓名与手机号，否则无法正常报名<br></br>预付款将于【开课后】24小时内自动退还
          </div>
        </Form.Header>
        <Form.Item
          name="name"
          label="姓名"
          layout="horizontal"
          rules={rules.name}
        >
          <Input />
        </Form.Item>
        <Form.Item name="gender" label="性别" rules={rules.gender}>
          <Radio.Group>
            <Space style={{ '--gap': '17px' }}>
              <Radio value="男">男</Radio>
              <Radio value="女">女</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="mobile"
          label="手机号"
          layout="horizontal"
          rules={rules.mobile}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="score"
          label="笔试分数"
          layout="horizontal"
          rules={rules.score}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="is_zhuangyuan"
          label="是否守擂"
          rules={rules.is_zhuangyuan}
        >
          <Radio.Group
            onChange={(value) =>
              handleIsZhuangyuanChange(value as 'true' | 'false')
            }
          >
            <Space style={{ '--gap': '17px' }}>
              <Radio value={'true'}>守擂</Radio>
              <Radio value={'false'}>攻擂</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="agreement"
          rules={rules.agreement}
          hidden={isZhuangyuan === undefined}
          label="协议选择"
          required
        >
          <Selector
            options={AgreementOptions}
            columns={2}
            style={{
              '--gap-horizontal': '27px',
              paddingInline: '31px',
            }}
          />
        </Form.Item>
        <Form.Item>
          <Radio checked={isAgree} onClick={() => setIsAgree(!isAgree)}>
            <span> 我同意 </span>
          </Radio>
          <a
            onClick={() =>
              Modal.confirm({
                title: '政策内容',
                content: <PrivacyPolicy />,
                confirmText: '我已知晓',
                cancelText: '',
              })
            }
          >
            《隐私政策》
          </a>
        </Form.Item>
        <div className="m-[12px] mt-0">
          <Button
            block
            type="submit"
            color="primary"
            size="large"
            disabled={
              isLoading ||
              creatingOrder ||
              (hasInventoryList?.[0]?.hasInventory ?? 0) <= 0
            }
            loading={creatingOrder}
          >
            确认订单
          </Button>
        </div>
      </Form>
    </Body>
  )
  // #endregion
}
// #region other
const PATH = '/enroll/inputting/$course_id'
enum positionCodeText {
  select = '请选择职位',
  loading = '正在加载',
}
/**
 * Row 组件的 Props 类型定义
 */
interface RowProps {
  index: number
  positionCodeList: PositionCode[]
  hasInventoryList: hasInventoryList
}
/**
 * PositionCodePopup 组件的 Props 类型定义
 */
interface PositionCodePopupProps {
  popupVisible: boolean
  setPopupVisible: (visible: boolean) => void
  handleSelect: (selectedValue: number) => void
  positionCodeList: PositionCode[]
  isLoading: boolean
  loadingView: ReactNode
}

/**
 * 处理表单提交逻辑，创建订单并返回订单 ID
 * @param value - 表单提交的值
 * @param courseId - 当前报名课程的 ID
 * @returns 新创建订单的 ID
 */
const handleSubmit = async (ctx: {
  value: DraftObj
  courseId: Course['courseId']
  setPending: (pending: boolean) => void
}): Promise<Order['id'] | null> => {
  const { value, courseId, setPending } = ctx
  if (isCreateLocked(courseId)) {
    Toast.show({
      content: '正在创建订单，请勿重复提交',
    })
    return null
  }
  lockCreate(courseId)
  setPending(true)
  const [agreement] = value.agreement ?? []
  // 按理来说这个错误不可能触发
  if (!agreement) {
    Toast.show({
      content: '请重新选择协议',
    })
    throw new Error('协议为空')
  }
  const isZhuangyuan = value.is_zhuangyuan === 'true' ? true : false
  const expectedAgreement = isZhuangyuan
    ? AgreementType.ZHUANGYUAN
    : AgreementType.NONZHUANGYUAN
  if (
    agreement !== AgreementType.NONAGREEMENT &&
    agreement !== expectedAgreement
  ) {
    Toast.show({
      content: '网络繁忙，请稍后重试',
    })
    throw new Error(
      '状元类型与协议类型不匹配' + JSON.stringify({ isZhuangyuan, agreement }),
    )
  }
  // 创建订单前添加校验，
  try {
    const rawData = (await apiClient.post('/order/create', {
      courseId: courseId,
      positionCodeId: value.position_code_id,
      userName: value.name,
      userGender: value.gender,
      userPhone: value.mobile,
      examScore: value.score,
      isZhuangyuan,
      regionId: value.region_id,
      agreementType: agreement,
    })) as {
      orderId: number
    }
    return rawData.orderId
  } catch (error: ApiError | any) {
    if (error?.msg === ErrorMsg.POSITION_INVENTORY_SHORTAGE) {
      Toast.show({
        content: '岗位库存不足',
        afterClose: () => setPending(false),
      })
      return null
    }
    if (error?.msg === ErrorMsg.COURSE_TOTAL_INVENTORY_SHORTAGE) {
      Toast.show({
        content: '该地区课程名额已满',
        afterClose: () => setPending(false),
      })
      return null
    }
    if (error?.msg === ErrorMsg.DUPLICATE_ORDER_SUBMISSION) {
      Toast.show({
        content: '您已提交过订单，无需重复提交',
        afterClose: () => setPending(false),
      })
      return null
    }
    if (error?.msg === ErrorMsg.COURSE_NON_PREMIUM_INVENTORY_SHORTAGE) {
      // 等待 3 秒
      await new Promise((resolve) => setTimeout(resolve, 3000))
      Toast.show({
        content: '系统繁忙',
        afterClose: () => setPending(false),
      })
      return null
    }
    if (error?.msg === ErrorMsg.BLACKLIST || error?.msg.includes('黑名单')) {
      // 等待 3 秒
      await new Promise((resolve) => setTimeout(resolve, 3000))
      Toast.show({
        content: '系统繁忙',
        afterClose: () => setPending(false),
      })
      return null
    }
    Toast.show({
      content: error?.msg,
      afterClose:()=>setPending(false),
    })
    throw error
  }
  finally {
    unlockCreate(courseId)
  }
}

/**
 * 渲染职位代码列表中的单行信息
 * @param props - 组件属性，包含索引和职位代码列表
 * @returns 渲染后的 JSX 元素，若索引超出列表长度则返回 null
 */
const Row: React.FC<RowProps> = ({
  index,
  positionCodeList,
  hasInventoryList,
}) => {
  if (positionCodeList.length <= index) {
    return null
  }
  const positionCode = positionCodeList[index]
  const hasInventory =
    hasInventoryList.find((item) => item.id === positionCode.id)
      ?.hasInventory ?? 0
  return (
    <div
      className={`py-3 border-b-[1px] border-[#eee] text-[17px] text-[#333] flex items-start flex-col ${hasInventory === 0 ? `text-[#A3A3A3]` : ``}`}
    >
      {positionCode.name.split('%').map((item, index) => (
        <span key={index}>{item}</span>
      ))}
    </div>
  )
}

/**
 * 根据搜索值过滤职位代码列表
 * @param codeList - 原始职位代码列表
 * @param searchValue - 搜索输入的值
 * @returns 过滤后的职位代码列表
 */
const filterFn = (
  codeList: PositionCode[],
  searchValue: string,
): PositionCode[] => {
  const trimSearchValue = searchValue.trim()
  if (!trimSearchValue) return []
  return codeList.filter((item) => item.name.includes(trimSearchValue))
}

/**
 * 职位代码选择弹窗组件
 * @param props - 组件属性，包含弹窗显示状态、关闭弹窗函数、选择处理函数和职位代码列表
 * @returns 渲染后的 JSX 元素
 */
const PositionCodePopup: React.FC<
  PositionCodePopupProps & { batchId: number | undefined }
> = ({
  popupVisible,
  setPopupVisible,
  handleSelect,
  positionCodeList,
  isLoading,
  loadingView,
  batchId,
}) => {
  // 搜索框输入值状态
  const [searchBarValue, setSearchBarValue] = useState('')
  const debouncedSetSearchBarValue = useCallback(
    debounce(setSearchBarValue, 500),
    [],
  )
  // 过滤后的职位代码列表
  const filteredPositionCodeList = filterFn(positionCodeList, searchBarValue)
  const displayList = filteredPositionCodeList.slice(0, 50)
  const positionCodeIdList = displayList.map(item => item.id)
  const hasMore = filteredPositionCodeList.length > 50
  const { data: hasInventoryList = [], isLoading: hasInventoryListIsloading } =
    usePositionCodeListHasInventory(positionCodeIdList, batchId)


  const listView = (
    <div className="overflow-y-auto" style={{ maxHeight: '100%' }}>
      {/* <div className="text-[14px] text-[#f35353] text-center">同一个职位代码可能有多个职位，请仔细甄别</div> */}
      {displayList.map((positionCode, index) => (
        <div
          key={positionCode.id}
          onClick={async () => {
            if (hasInventoryListIsloading) {
              EnrollToast.loadingInventory()
              return
            }
            if (
              hasInventoryList.find((item) => item.id === positionCode.id)
                ?.hasInventory === 0
            ) {
              EnrollToast.full()
              return
            }
            const positionCodeNameItems = positionCode.name.split('%')
            const [code, workplace, name] = positionCodeNameItems
            const handler = Modal.show({
              title: '请确认岗位信息',
              content: (
                <div>
                  <div>代码：{code}</div>
                  <div>单位：{workplace}</div>
                  <div>职位：{name}</div>
                </div>
              ),
              actions: [
                {
                  key: 'confirm',
                  text: '本人承诺，本人已真实报考该岗位',
                  primary: true,
                  style: { fontSize: '14px' },
                  onClick: () => {
                    handleSelect(positionCode.id)
                    setPopupVisible(false)
                    handler.close()
                  },
                },
                {
                  key: 'cancel',
                  text: '取消',
                  style: { fontSize: '14px' },
                  onClick: () => {
                    handler.close()
                  },
                },
              ],
            })
          }}
        >
          <Row
            index={index}
            positionCodeList={displayList as PositionCode[]}
            hasInventoryList={hasInventoryList}
          />
        </div>
      ))}
      {hasMore && (
        <div className="text-center py-3 text-[#A3A3A3] text-[14px]">
          结果过多，仅显示前50条，请输入更精确的关键词
        </div>
      )}
    </div>
  )
  return (
    <Popup
      visible={popupVisible}
      onClose={() => {
        setPopupVisible(false)
      }}
      bodyStyle={{
        position: 'fixed',
        width: '100%',
        left: 0,
        right: 0,
        marginInline: 'auto',
        paddingTop: '15px',
        paddingInline: '12px',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 固定在顶部的搜索区域 */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 100,
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <p className="relative text-center">
          <span
            onClick={() => setPopupVisible(false)}
            className="left-[10px] top-0 bottom-0 my-[auto] absolute text-[#1677FF] text-[15px] flex items-center"
          >
            取消
          </span>
          <span className="text-[18px]">职位代码</span>
        </p>
        <SearchBar
          placeholder="搜索"
          style={{ margin: '8px 0' }}
          onSearch={(value) => {
            debouncedSetSearchBarValue(value)
          }}
          onChange={(value) => {
            debouncedSetSearchBarValue(value)
          }}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          loadingView
        ) : searchBarValue.trim() === '' ? (
          <div className="text-[#A3A3A3] text-[15px] text-center">
            请在搜索框中输入职位代码
          </div>
        ) : filteredPositionCodeList.length === 0 ? (
          <div className="text-[#A3A3A3] text-[15px] text-center">
            未找到匹配的职位代码，请正确输入职位代码
          </div>
        ) : (
          listView
        )}
      </div>
    </Popup>
  )
}
// #endregion

export const Route = createFileRoute(PATH)({
  component: RouteComponent,
})
